import type { GroupAnalysisSpanData } from '@/generated/types/navigator/types.gen';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@/components';
import { NetworkType } from '@/features/types';
import { getAppConfig } from '@/app/config';
import { useAppDispatch } from '@/app/hooks';
import { clearItem, setListView } from '@/features/analysisMapSlice';
import { ButtonComponent } from '@/components/Button/Button';
import { TabsComponent } from '@/components/Tabs/Tabs';
import {
  openWorklogModal,
  WorklogModalMode,
} from '@/features/worklogModalSlice';
import TagManager from 'react-gtm-module';
import { NetworkTab } from './NetworkTab';
import { ActivityTab } from './ActivityTab';
import { RiskTab } from './RiskTab';
import { Text, Title, useMantineTheme } from '@mantine/core';
import { ArrowLeft, ChevronDown, ChevronUp, FilePlus } from 'react-feather';
import classes from './SpanDetailContent.module.css';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { SpanSupportActions } from '@/views/map/popover/SpanSupportActions';
import { GoogleEvents } from '@/features/googleEvents';
import { StatusAndTagsRow } from './StatusAndTagsRow';
import { withForwardedParams } from '@/features/utils';

const WorklogTabs = {
  Risk: 'Risk',
  Network: 'Network',
  Activity: 'Activity',
};

/** Controls passed from the Drawer to animate the corner chevron. */
interface DrawerControls {
  isPreview: boolean;
  onToggle: () => void;
}

/**
 * Hook that returns the rendered pieces of span detail content (header and tabs).
 * Used by both SpanMapPopover (mobile Drawer) and SpanDetailSidebar (desktop sidebar).
 *
 * The header includes an "Add activity" button that opens the worklog modal.
 * When a worklog is added, the hook automatically switches to the Activity tab
 * to show the newly created entry.
 */
export function useSpanDetailContent(
  a: GroupAnalysisSpanData,
  options?: { showBackArrow?: boolean }
) {
  const dispatch = useAppDispatch();
  const networkLabel = NetworkType[a.network ?? 1];
  const theme = useMantineTheme();
  const intl = useClientTerminology();
  const scrollingRef = useRef<HTMLDivElement>(null);
  const [worklogAdded, setWorklogAdded] = useState(false);

  const tabOptions = [
    { header: WorklogTabs.Risk, Component: <RiskTab {...a} /> },
    { header: WorklogTabs.Network, Component: <NetworkTab {...a} /> },
    { header: WorklogTabs.Activity, Component: <ActivityTab a={a} /> },
  ];

  const [activeTab, setActiveTab] = useState<string>(WorklogTabs.Risk);

  useEffect(() => {
    if (worklogAdded) {
      setActiveTab((prev) => {
        if (prev !== WorklogTabs.Activity) {
          return WorklogTabs.Activity;
        }
        return prev;
      });
      setWorklogAdded(false);
    }
  }, [worklogAdded]);

  const baseItemUrl = `${getAppConfig().appUrl}/group/${a.group}`;
  const itemUrl = withForwardedParams(baseItemUrl);

  const spanName = a.displayName ?? a.level3;

  /**
   * Build the header JSX.
   * When `drawerControls` is provided (mobile drawer), the corner chevron
   * toggles the drawer between preview ↔ open.
   * When omitted (desktop sidebar), no chevron is rendered.
   */
  const getHeader = (drawerControls?: DrawerControls) => (
    <>
      <div className={classes.header}>
        {options?.showBackArrow && (
          <IconButton
            Component={ArrowLeft}
            onClick={() => {
              dispatch(clearItem());
              dispatch(setListView(true));
            }}
            color={theme.other.mainColor.foreground.primary}
            iconSize={24}
            data-testid="back-arrow-button"
          />
        )}
        <div className={classes.mainHeaderContent}>
          {/* Row 1: Span name + optional drawer toggle chevron */}
          <div className={classes.spanNameRow}>
            <Title order={4} fz="xl" lh="lg" fw={600} lineClamp={1}>
              {spanName}
            </Title>
            {drawerControls && (
              <IconButton
                Component={drawerControls.isPreview ? ChevronUp : ChevronDown}
                onClick={drawerControls.onToggle}
                color={theme.other.mainColor.foreground.primary}
                iconSize={16}
                data-testid="drawer-toggle-chevron"
              />
            )}
          </div>
          {/* Row 2: Network type */}
          <Text fz="sm" lh="sm" c="dimmed">
            {networkLabel} span
          </Text>
          {/* Row 3: Work status + plan tags */}
          <StatusAndTagsRow
            workStatus={a.workStatus}
            lists={a.lists}
            level2={a.level2 ?? undefined}
            level3={a.level3}
          />
        </div>
      </div>
      <div className={classes.fieldInfoWrapper}>
        <ButtonComponent
          label={intl.formatMessage({
            id: 'spanMapPopover.addActivity',
          })}
          variant="pill"
          size="sm"
          leftIconComponent={FilePlus}
          onClick={() => {
            dispatch(
              openWorklogModal({
                mode: WorklogModalMode.ADD,
                spanId: a.level3,
              })
            );
            TagManager.dataLayer({
              dataLayer: {
                event: GoogleEvents.worklogAddFieldInfo,
              },
            });
          }}
        />

        {a.level2 && (
          <SpanSupportActions
            lists={a.lists}
            level2={a.level2 ?? undefined}
            level3={a.level3}
            spanName={spanName}
            spanUrl={itemUrl}
            key={`${a.level2}-${a.level3}`}
          />
        )}
      </div>
    </>
  );

  const tabs = (
    <TabsComponent
      tabs={tabOptions}
      variant="drawer"
      ref={scrollingRef}
      isScrollable
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );

  return { getHeader, tabs, scrollingRef };
}
